import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { programmeService, departmentService } from "../services/api";
import { toast } from "react-hot-toast";
import "./Programme.css";

const Programme = () => {
  const { user } = useContext(AuthContext);
  const [programmes, setProgrammes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    shortName: "",
    department: "",
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (user?.role === "SuperAdmin") {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [progRes, deptRes] = await Promise.all([
        programmeService.getAll(),
        departmentService.getAll(),
      ]);
      setProgrammes(progRes.data);
      setDepartments(deptRes.data);
      setError("");
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // client-side validation for shortName length (3-16 chars)
    const shortLen = (formData.shortName || "").trim().length;
    if (shortLen < 3 || shortLen > 16) {
      setError("Short Name must be between 3 and 16 characters");
      return;
    }

    try {
      if (editingId) {
        if (
          !window.confirm(
            "Are you sure you want to update this programme's details?",
          )
        )
          return;
        await programmeService.update(editingId, formData);
      } else {
        await programmeService.create(formData);
      }
      setFormData({ name: "", shortName: "", department: "" });
      setEditingId(null);
      setShowForm(false);
      toast.success(editingId ? "Programme updated!" : "Programme created!");
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save programme";
      setError(msg);
      toast.error(msg);
    }
  };

  const handleEdit = (programme) => {
    setFormData(programme);
    setEditingId(programme._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this programme? This action cannot be undone.",
      )
    ) {
      try {
        await programmeService.delete(id);
        toast.success("Programme deleted");
        fetchData();
      } catch (err) {
        toast.error("Failed to delete programme");
      }
    }
  };

  if (loading)
    return (
      <div className="programme-page">
        <p>Loading...</p>
      </div>
    );

  return (
    <div className="programme-page">
      <div className="programme-page-header">
        <h1>Programme Management</h1>
        {user?.role === "SuperAdmin" && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="primary-action-btn"
          >
            {showForm ? "Cancel" : "Add Programme"}
          </button>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {showForm && user?.role === "SuperAdmin" && (
        <form onSubmit={handleSubmit} className="programme-form-card">
          <div className="form-group-item">
            <label>Programme Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              placeholder="e.g. Master of Computer Applications"
            />
          </div>
          <div className="form-group-item">
            <label>Short Name</label>
            <input
              type="text"
              value={formData.shortName}
              onChange={(e) =>
                setFormData({ ...formData, shortName: e.target.value })
              }
              required
              minLength="3"
              maxLength="16"
              placeholder="3-16 chars (e.g. MCA)"
            />
          </div>
          <div className="form-group-item">
            <label>Department</label>
            <select
              value={formData.department}
              onChange={(e) =>
                setFormData({ ...formData, department: e.target.value })
              }
              required
            >
              <option value="">Select Department</option>
              {departments.map((dept) => (
                <option key={dept._id} value={dept._id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="primary-action-btn"
            style={{ width: "100%" }}
          >
            {editingId ? "Update Programme" : "Create Programme"}
          </button>
        </form>
      )}

      <div className="programme-table-card">
        <div className="table-wrapper">
          <table className="programme-table">
            <thead>
              <tr>
                <th>Programme Name</th>
                <th>Short Name</th>
                <th>Department</th>
                {user?.role === "SuperAdmin" && (
                  <th style={{ textAlign: "right" }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {programmes.length > 0 ? (
                programmes.map((prog) => (
                  <tr key={prog._id}>
                    <td>
                      <strong>{prog.name}</strong>
                    </td>
                    <td>{prog.shortName}</td>
                    <td>{prog.department?.name || "-"}</td>
                    {user?.role === "SuperAdmin" && (
                      <td style={{ textAlign: "right" }}>
                        <div className="programme-actions">
                          <button
                            onClick={() => handleEdit(prog)}
                            className="edit-action-btn"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(prog._id)}
                            className="delete-action-btn"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={user?.role === "SuperAdmin" ? 4 : 3}
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#64748b",
                    }}
                  >
                    No programmes found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Programme;
